import { useMemo } from 'react';
import { ApolloQueryResult, WatchQueryFetchPolicy } from '@apollo/client';

import { OfferFragment, OffersQuery, OffersQueryVariables, useOffersQuery } from '@ocx/graphql';
import { usePersistQueryResult } from '@ocx/utils';

import { Offer } from './offer.types';
import { mapFragmentsToSortedOffersList } from './offer.dto';
import { OPTIMIZED_OFFERS_FILTER } from './offers.constants';
import { setCachedOffersQuery } from './offers.cache';

export type UseOffersVariables = Partial<OffersQueryVariables>;

export type UseOffersReturn = {
  offers: Offer[];
  /** Apollo's raw `loading` — `true` while any request is in flight (initial or revalidation). */
  loading: boolean;
  refetch: () => Promise<ApolloQueryResult<OffersQuery>>;
};

export type UseOffersParams = {
  variables?: UseOffersVariables;
  skip?: boolean;
  fetchPolicy?: WatchQueryFetchPolicy;
  persistToStorage?: boolean;
};

export const useOffers = (params: UseOffersParams = {}): UseOffersReturn => {
  const { variables: variablesParam, skip, fetchPolicy = 'cache-and-network', persistToStorage = false } = params;

  const variables: OffersQueryVariables = {
    locationsGeoFilter: null,
    ...OPTIMIZED_OFFERS_FILTER,
    ...variablesParam,
  };

  const { data, loading, refetch } = useOffersQuery({
    fetchPolicy,
    skip,
    variables,
  });

  usePersistQueryResult({
    data,
    variables,
    persist: setCachedOffersQuery,
    enabled: persistToStorage && !skip,
    loading,
  });

  const offers = useMemo((): Offer[] => {
    if (!data?.customer?.membership?.offers) {
      return [];
    }

    const offerFragments = data.customer.membership.offers.edges
      .filter((edge): edge is { node: OfferFragment } => !!edge?.node)
      .map(({ node }) => node);

    return mapFragmentsToSortedOffersList(offerFragments);
  }, [data]);

  return { offers, loading, refetch };
};
